import React, { createContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Divider, Tooltip } from 'antd';

import './LeftBar.scss';

import { useAuth } from '../../../../../features/auth/context/AuthContext';
import Profile from 'scenes/Private/Profile/Profile';
import Entities from 'scenes/Private/Entities/Entities';
import Loans from 'scenes/Private/Loans/Loans';
import { BorrowersIcon, LogOutIcon } from './icons';
import { computeInitials } from './helpers';

const avatarColors: string[] = ["purple", "yellow", "blue"];
const colorSelected: string = avatarColors[Math.floor(Math.random() * avatarColors.length)];

export const ProfileViewContext = createContext<{ visible: boolean; setHandlerVisible: any } | {}>({});
export const EntitiesViewContext = createContext<{ visible: boolean; setHandlerVisible: any } | {}>({});
export const LoansViewContext = createContext<{ visible: boolean; setHandlerVisible: any } | {}>({});

interface ProcessingLeftBarProps {
  borrowerData?: {
    name?: string;
    lastName?: string;
    email?: string;
  };
}

const ProcessingLeftBar: React.FC<ProcessingLeftBarProps> = (props) => {
  const { borrowerData } = props;
  const { isAdmin, isBroker, user, logout, isAuthenticated } = useAuth();

  const [entitiesView, setEntitiesView] = useState<boolean>(false);
  const [loansView, setLoansView] = useState<boolean>(false);
  const [profileView, setProfile] = useState<boolean>(false);

  const onLogout = (): void => {
    logout();
    window.location.reload();
  }

  const activeOption = (visible: 'loans' | 'entities' | 'profile') => {
    if (isAdmin) {
      return;
    }

    setLoansView(visible === 'loans');
    setEntitiesView(visible === 'entities');
    setProfile(visible === 'profile');
  }

  const avatarInitials = computeInitials(borrowerData, user);

  // Only show avatar if we have real user data (not just fallback "CL")
  // This prevents showing avatar when in broker mode without logged in user
  // But allows it when a broker is authenticated with real data
  const shouldShowAvatar = isAuthenticated && avatarInitials !== 'CL';

  return (
    <EntitiesViewContext.Provider value={{ visible: entitiesView, setHandlerVisible: setEntitiesView }}>
      <LoansViewContext.Provider value={{ visible: loansView, setHandlerVisible: setLoansView }}>
        <ProfileViewContext.Provider value={{ visible: profileView, setHandlerVisible: setProfile }}>
          <div className="left-bar">
            <div className="box"></div>

            {shouldShowAvatar && (
              <div style={{ width: 40, margin: 'auto' }}>
                {!isAdmin && (
                  <Divider style={{ background: '#ccd3db', margin: 0 }} />
                )}
              </div>
            )}

            {!isAdmin && shouldShowAvatar && (
              <div className="box box-medium">
                <div className="menu-item">
                  <Link className={`link  link-with-hover ${loansView && 'link-active'}`} to="./" onClick={() => activeOption('loans')}>
                    <div className="brief" />
                    <span>Loans</span>
                  </Link>
                  <Link className={`link  link-with-hover ${entitiesView && 'link-active'}`} to="./" onClick={() => activeOption('entities')}>
                    <div className="award" />
                    <span>Entities</span>
                  </Link>
                </div>
              </div>
            )}

            {!isAdmin && shouldShowAvatar && (
              <div style={{ width: 40, margin: 'auto' }}>
                <Divider style={{ background: '#ccd3db', margin: 0 }} />
              </div>
            )}

            {shouldShowAvatar && (
              <div className="box box-end">
                <div className='icon logout' onClick={onLogout}>
                  <LogOutIcon />
                  <div>
                    Log Out
                  </div>
                </div>

                {isAdmin && (
                  <Tooltip
                    placement="right"
                    color='#5f8db8'
                    title={<div><p>Logged in as</p><span>{user?.email}</span></div>}
                  >
                    <div className='icon profile'>
                      <BorrowersIcon />
                      <div>Profile</div>
                    </div>
                  </Tooltip>
                )}

                {!isAdmin && (
                  <Link className={`link  link-with-hover ${profileView && 'link-active'}`} to="./" onClick={() => activeOption('profile')}>
                    <Avatar
                      size={35}
                      className={`avatar avatar-${colorSelected}`}
                    >
                      {avatarInitials}
                    </Avatar>
                  </Link>
                )}
              </div>
            )}
          </div>
          {entitiesView && <Entities EntitiesViewContext={EntitiesViewContext} useRedirectToDashboard />}
          {loansView && <Loans LoansViewContext={LoansViewContext} />}
          <Profile
            ProfileViewContext={ProfileViewContext}
            EntitiesViewContext={EntitiesViewContext}
            LoansViewContext={LoansViewContext}
          />
        </ProfileViewContext.Provider>
      </LoansViewContext.Provider>
    </EntitiesViewContext.Provider>
  );
};

export default ProcessingLeftBar;